import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ContestsSection } from '@/components/Contests';
import axios from 'axios';
import {
  ArrowRight, User, BookOpen, CheckCircle, GraduationCap, ClipboardList,
  Lightbulb, BookOpenCheck, MonitorPlay, LogOut, ChevronUp, MessageSquare,
  Target, CalendarDays, BarChart3, Award, Camera, Trophy, StickyNote, Eye,
  Clock, Download, Paperclip, Medal,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOOL_META = {
  'tips': { label: 'محتوى الدرس', c: 'var(--med)', Icon: Lightbulb },
  'teach-guide': { label: 'دليل التدريس', c: 'var(--pu)', Icon: GraduationCap },
  'worksheet': { label: 'ورقة عمل', c: 'var(--easy)', Icon: ClipboardList },
  'quiz': { label: 'اختبار', c: 'var(--pu)', Icon: Target },
  'terms': { label: 'المصطلحات', c: 'var(--pk)', Icon: BookOpen },
  'explain-lesson': { label: 'شرح الدرس', c: 'var(--pu)', Icon: BookOpenCheck },
  'story': { label: 'القصة', c: 'var(--med)', Icon: BookOpenCheck },
  'video': { label: 'الفيديو', c: 'var(--hard)', Icon: MonitorPlay },
};
const TOOL_LABELS = { quiz: 'الاختبار', worksheet: 'ورقة العمل' };

const CHAT_META = {
  'طلب إجابة مباشرة': { c: 'var(--hard)', desc: 'يطلب الحل أو الإجابة مباشرة بدون محاولة التفكير' },
  'طلب توضيح': { c: 'var(--pu)', desc: 'يسأل عن معنى مفهوم أو تفسير فكرة — علامة جيدة على الرغبة في الفهم' },
  'توضيح': { c: 'var(--pu)', desc: 'يسأل عن توضيح محتوى الدرس' },
  'استكشاف وفضول': { c: 'var(--easy)', desc: 'يسأل أسئلة إضافية ويستكشف — دليل على فضول علمي ممتاز' },
  'خارج الموضوع': { c: 'var(--med)', desc: 'أسئلة غير مرتبطة بمحتوى الدرس' },
  'محادثة عامة': { c: 'var(--med)', desc: 'تحية أو محادثة غير مرتبطة مباشرة بالدرس' },
};

const LEVELS = [['مبتدئ', 0], ['متعلم', 80], ['متميز', 200], ['خبير', 500]];
const col = (p) => (p < 50 ? 'var(--hard)' : p < 75 ? 'var(--med)' : 'var(--easy)');

const Ring = ({ pct, size, stroke, color, children }) => {
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const offset = C * (1 - Math.min(pct, 100) / 100);
  const h = size / 2;
  return (
    <div className="rg" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle className="tk" cx={h} cy={h} r={r} strokeWidth={stroke} />
        <circle className="fl" cx={h} cy={h} r={r} strokeWidth={stroke}
          transform={`rotate(-90 ${h} ${h})`}
          style={{ stroke: color, strokeDasharray: C, strokeDashoffset: offset }} />
      </svg>
      <div className="in">{children}</div>
    </div>
  );
};

const Head = ({ Icon, title, sub, c, tr }) => (
  <div className="hd" style={c ? { '--c': c } : undefined}>
    <div className="t"><Icon /></div>
    <div><h2>{title}</h2>{sub && <p>{sub}</p>}</div>
    {tr && <span className="tr">{tr}</span>}
  </div>
);

const Chip = ({ toolKey, count }) => {
  const m = TOOL_META[toolKey] || { label: toolKey, c: 'var(--pu)', Icon: CheckCircle };
  const { Icon } = m;
  return (
    <span className="cp" style={{ '--c': m.c }}>
      {count != null && <em>{count}</em>}<Icon />{m.label}
    </span>
  );
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedScores, setExpandedScores] = useState({});
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [olympicsStats, setOlympicsStats] = useState(null);
  const [teacherContent, setTeacherContent] = useState(null);
  const [profileTab, setProfileTab] = useState('overview');
  const [studentGrades, setStudentGrades] = useState([]);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/progress/summary`, { headers: getAuthHeaders() });
        setData(res.data);
        setNewName(res.data.student_name || '');
        if (res.data.avatar_path) {
          try {
            const avatarRes = await axios.get(`${API_URL}/api/profile/avatar/${res.data.avatar_path}`, {
              headers: getAuthHeaders(), responseType: 'blob'
            });
            setAvatarUrl(URL.createObjectURL(avatarRes.data));
          } catch {}
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
    axios.get(`${API_URL}/api/olympics/profile-stats`, { headers: getAuthHeaders() }).then(r => setOlympicsStats(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/student/teacher-content`, { headers: getAuthHeaders() }).then(r => setTeacherContent(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/student/grades`, { headers: getAuthHeaders() }).then(r => setStudentGrades(r.data)).catch(() => {});
    return () => { if (avatarUrl) URL.revokeObjectURL(avatarUrl); };
  }, [getAuthHeaders]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/profile/avatar`, formData, { headers: getAuthHeaders() });
      setAvatarUrl(URL.createObjectURL(file));
    } catch {
      alert('حدث خطأ في رفع الصورة');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) return;
    setSavingName(true);
    try {
      await axios.put(`${API_URL}/api/profile/update`, { name: newName.trim() }, { headers: getAuthHeaders() });
      setData(prev => ({ ...prev, student_name: newName.trim() }));
      setEditingName(false);
    } catch {
      alert('حدث خطأ');
    } finally {
      setSavingName(false);
    }
  };

  const stats = data?.stats || {};
  const lvl = stats.level;
  const lvPct = lvl?.next_xp ? Math.min(100, Math.round((lvl.xp / lvl.next_xp) * 100)) : 100;
  const curLevelIdx = lvl ? LEVELS.reduce((acc, l, i) => (lvl.xp >= l[1] ? i : acc), 0) : 0;

  const TABS = [
    { id: 'overview', label: 'نظرة عامة', Icon: BarChart3 },
    { id: 'olympics', label: 'الأولمبياد', Icon: Trophy },
    { id: 'weekly-plans', label: 'الخطة الأسبوعية', Icon: CalendarDays },
    { id: 'assignments', label: 'الواجبات', Icon: ClipboardList },
    { id: 'notes', label: 'ملاحظات المعلمين', Icon: StickyNote },
    { id: 'contests', label: 'المسابقات', Icon: Award },
    { id: 'grades-tab', label: 'كشف العلامات', Icon: BarChart3 },
  ];

  // Activity heatmap (last 21 days)
  const activeSet = new Set(stats.activity_dates || []);
  const days = [];
  const dayNames = ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'];
  let activeCount = 0;
  for (let i = 20; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const ds = d.toISOString().split('T')[0];
    const on = activeSet.has(ds);
    if (on) activeCount++;
    days.push({ ds, on, today: i === 0, num: d.getDate(), dn: dayNames[d.getDay()] });
  }
  const consistency = Math.round((activeCount / 21) * 100);

  const ol = stats.olympics;
  const olSubjects = ol?.subjects?.length ? ol.subjects : null;

  return (
    <div className="prpage" dir="rtl" data-testid="profile-page">
      <header className="nav">
        <Link to="/grades" className="bk" data-testid="back-grades"><ArrowRight style={{ transform: 'scaleX(-1)' }} />الصفوف</Link>
        <h1>الملف الشخصي</h1>
        <button className="lo" onClick={handleLogout} title="تسجيل الخروج" data-testid="logout-button"><LogOut /></button>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      {loading ? (
        <div className="wrap">
          <aside><div className="sk" style={{ height: 220 }} /><div className="sk" style={{ height: 260 }} /></aside>
          <div className="mn"><div className="sk" style={{ height: 56 }} /><div className="sk" style={{ height: 220 }} /><div className="sk" style={{ height: 180 }} /></div>
        </div>
      ) : (
        <div className="wrap">
          {/* ===== Sidebar ===== */}
          <aside>
            {/* Profile card */}
            <section className="cd pf" data-testid="profile-card">
              <div className="pf-b" aria-hidden="true"><i></i><i></i><i></i></div>
              <div className="pf-c">
                <label className="av" data-testid="avatar-upload">
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                  {avatarUrl ? <img src={avatarUrl} alt="" /> : (data?.student_name || user?.name || '؟').charAt(0)}
                  <span className="cam">{uploadingAvatar ? '...' : <Camera />}</span>
                </label>
                {editingName ? (
                  <div className="nameedit">
                    <input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} data-testid="edit-name-input" />
                    <button className="mini ok" onClick={handleSaveName} disabled={savingName} data-testid="save-name-btn">{savingName ? '...' : 'حفظ'}</button>
                    <button className="mini no" onClick={() => { setEditingName(false); setNewName(data?.student_name || ''); }}>إلغاء</button>
                  </div>
                ) : (
                  <h2 onClick={() => setEditingName(true)} title="اضغط لتعديل الاسم" style={{ cursor: 'pointer' }} data-testid="profile-name">
                    {data?.student_name || user?.name}
                  </h2>
                )}
                <small>@{data?.username || user?.username}</small>
                {lvl && <span className="lvp"><Medal />{lvl.name}</span>}
              </div>
            </section>

            {/* Level card */}
            {lvl && (
              <section className="cd" data-testid="level-section">
                <div className="lvc">
                  <Ring pct={lvPct} size={138} stroke={11} color="var(--pk)">
                    <span className="big">{lvl.num}</span><span className="sm">المستوى</span>
                  </Ring>
                  <div className="xpl">
                    <b>{lvl.xp}</b> نقطة خبرة
                    {lvl.next_xp && <small>{lvl.next_xp - lvl.xp} نقطة للمستوى التالي</small>}
                    {!lvl.next_xp && <small>وصلت لأعلى مستوى!</small>}
                  </div>
                  <div className="stp">
                    {LEVELS.map((l, i) => (
                      <div key={l[0]} className={`${i <= curLevelIdx ? 'd' : ''} ${i === curLevelIdx ? 'cu' : ''}`}>
                        <i></i><span>{l[0]}</span><span style={{ fontWeight: 600, opacity: .7 }}>{l[1]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {lvl.xp_breakdown?.some(b => b.total > 0) && (
                  <>
                    <div className="lb">كيف حصلت على النقاط</div>
                    <div className="pts">
                      {lvl.xp_breakdown.filter(b => b.total > 0).map(b => (
                        <div key={b.label}><span>{b.label} ({b.count} × {b.pts_each})</span><b>+{b.total}</b></div>
                      ))}
                    </div>
                  </>
                )}
              </section>
            )}

            {/* Side stats */}
            <div className="ss">
              <div className="sc" style={{ '--c': 'var(--pu)' }}><BookOpen /><b>{data?.total_lessons_touched || 0}</b><span>دروس مدروسة</span></div>
              <div className="sc" style={{ '--c': 'var(--easy)' }}><CheckCircle /><b>{data?.total_tools_completed || 0}</b><span>أنشطة مكتملة</span></div>
              <div className="sc" style={{ '--c': 'var(--med)' }}><Target /><b>{stats.total_attempts || 0}</b><span>محاولات اختبار</span></div>
              <div className="sc" style={{ '--c': 'var(--pk)' }}><CalendarDays /><b>{stats.active_days || 0}</b><span>أيام نشاط</span></div>
            </div>
          </aside>

          {/* ===== Main ===== */}
          <div className="mn">
            <div className="tabs" role="tablist" data-testid="profile-tabs">
              {TABS.map(t => (
                <button key={t.id} className="tb" role="tab" aria-selected={profileTab === t.id}
                  onClick={() => setProfileTab(t.id)} data-testid={`profile-tab-${t.id}`}>
                  <t.Icon />{t.label}
                </button>
              ))}
            </div>

            {/* ===== Overview ===== */}
            {profileTab === 'overview' && (
              <div className="pn" role="tabpanel">
                {/* Performance */}
                {(stats.total_attempts > 0 || data?.total_tools_completed > 0) && (
                  <section className="cd" data-testid="performance-section">
                    <Head Icon={BarChart3} title="تحليل الأداء" sub="المستوى الحالي بناءً على آخر محاولة لكل أداة" />
                    {stats.avg_score_pct > 0 && (
                      <div className="pf2">
                        <Ring pct={stats.avg_score_pct} size={150} stroke={13} color={col(stats.avg_score_pct)}>
                          <span className="pct" style={{ color: col(stats.avg_score_pct) }}>{stats.avg_score_pct}%</span>
                          <span className="lab">المستوى الحالي</span>
                        </Ring>
                        <div>
                          {stats.best_score && (
                            <div className="best">
                              <Medal />
                              <div>
                                <strong>أفضل نتيجة</strong>
                                <b dir="ltr">{stats.best_score.score}/{stats.best_score.total} ({stats.best_score.pct}%)</b>
                                <span>{TOOL_LABELS[stats.best_score.tool_type] || stats.best_score.tool_type}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {stats.tools_by_subject && Object.keys(stats.tools_by_subject).length > 0 && (
                      <>
                        <div className="lb">الأدوات المستخدمة حسب المادة</div>
                        <div className="sj">
                          {Object.entries(stats.tools_by_subject).map(([subject, tools]) => (
                            <div className="sj-r" key={subject}>
                              <h4><BookOpen />{subject}</h4>
                              <div className="cps">
                                {Object.entries(tools).sort((a, b) => b[1] - a[1]).map(([tool, count]) => (
                                  <Chip key={tool} toolKey={tool} count={count} />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {stats.subjects_list?.length > 0 && (
                      <>
                        <div className="lb">المواد التي تمت دراستها</div>
                        <div className="cps">
                          {stats.subjects_list.map(s => (
                            <span className="cp" key={s}><BookOpen />{s}</span>
                          ))}
                        </div>
                      </>
                    )}

                    {stats.total_attempts > 0 && (
                      <div className="tip">
                        <Lightbulb />
                        <div>
                          <h5>نصيحة لأولياء الأمور</h5>
                          <p>
                            {stats.avg_score_pct >= 80
                              ? `المستوى الحالي للطالب ممتاز (${stats.avg_score_pct}%)! استمروا في تشجيعه.`
                              : stats.avg_score_pct >= 50
                                ? `المستوى الحالي جيد (${stats.avg_score_pct}%). ننصح بمراجعة الأسئلة الخاطئة معه.`
                                : `الطالب يحتاج دعماً إضافياً (${stats.avg_score_pct}%). ننصح بإعادة الدرس معه وحل التمارين مرة أخرى.`}
                            {stats.active_days <= 1 && ' حاولوا استخدام المنصة بانتظام — 10 دقائق يومياً تصنع فرقاً.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </section>
                )}

                {/* Subject performance */}
                {stats.subject_stats?.length > 0 && (
                  <section className="cd" data-testid="subject-performance">
                    <Head Icon={BookOpen} title="أداء المواد" c="var(--pu)" />
                    <div className="sg2">
                      {stats.subject_stats.map(subj => (
                        <div className="sb2" key={subj.name} style={{ '--c': col(subj.pct) }}>
                          <Ring pct={subj.pct} size={70} stroke={7} color={col(subj.pct)}><b>{subj.pct}%</b></Ring>
                          <div>
                            <h4>{subj.name}</h4>
                            <small>{subj.attempts} محاولة</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Activity calendar */}
                {stats.activity_dates?.length > 0 && (
                  <section className="cd" data-testid="activity-chart">
                    <Head Icon={CalendarDays} title="سجل النشاط" sub="آخر 21 يومًا" />
                    <p className="dsc">الأيام الخضراء تعني أن الطالب استخدم المنصة في ذلك اليوم. الانتظام اليومي يساعد على تثبيت المعلومات.</p>
                    <div className="cal">
                      {days.map(d => (
                        <div key={d.ds} className={`dy ${d.on ? 'on' : ''} ${d.today ? 'td' : ''}`} title={d.ds}>
                          <b>{d.num}</b><span>{d.dn}</span>
                        </div>
                      ))}
                    </div>
                    <div className="lgd">
                      <div className="k"><span><s className="g"></s>نشط</span><span><s></s>غير نشط</span></div>
                      <div>الانتظام: <span className="rgp">{activeCount} من 21 يوم ({consistency}%)</span></div>
                    </div>
                  </section>
                )}

                {/* Chat analysis */}
                {stats.chat_analysis && (
                  <section className="cd" data-testid="chat-analysis">
                    <Head Icon={MessageSquare} title="تحليل محادثات الطالب مع الذكاء الاصطناعي" />
                    <p className="dsc" style={{ margin: 0 }}>يوضح كيف يستخدم الطالب خدمة «اسأل عن محتوى الدرس» — هل يطلب الإجابة مباشرة أم يحاول الفهم؟</p>
                    <div className="cm"><span>{stats.chat_analysis.total_messages} رسالة</span><span>في {stats.chat_analysis.lessons_chatted} درس</span></div>
                    <div className="sbar" role="img" aria-label="توزيع أنواع الرسائل">
                      {stats.chat_analysis.categories.map(cat => (
                        <i key={cat.name} style={{ '--c': (CHAT_META[cat.name] || { c: 'var(--pu)' }).c, width: `${cat.pct}%` }} />
                      ))}
                    </div>
                    <div className="lg2">
                      {stats.chat_analysis.categories.map(cat => {
                        const m = CHAT_META[cat.name] || { c: 'var(--pu)', desc: '' };
                        return (
                          <div className="lc" key={cat.name} style={{ '--c': m.c }}>
                            <h4>{cat.name}<span>{cat.count} ({cat.pct}%)</span></h4>
                            <p>{m.desc}</p>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Lessons progress timeline */}
                <h2 className="sec-t">تقدمك في الدروس</h2>
                {!data?.lessons?.length ? (
                  <section className="cd">
                    <div className="emp" data-testid="no-progress">
                      <div className="t"><BookOpen /></div>
                      <b>لم تبدأ أي درس بعد</b>
                      <span>ابدأ بفتح أي درس واستخدام أدوات الذكاء الاصطناعي</span>
                      <button className="bt" onClick={() => navigate('/grades')}>تصفح الصفوف</button>
                    </div>
                  </section>
                ) : (
                  <div className="tl" data-testid="progress-list">
                    {data.lessons.map(lesson => (
                      <section className="cd lsc" key={lesson.lesson_id} data-testid={`progress-lesson-${lesson.lesson_id}`}>
                        <div className="ls-h">
                          <div>
                            <h3 onClick={() => navigate(`/lessons/${lesson.lesson_id}`)}>{lesson.title}</h3>
                            <p>{lesson.grade_name}{lesson.subject_name ? ` - ${lesson.subject_name}` : ''}</p>
                          </div>
                          <span className="cnt">{lesson.completed_tools.length}<CheckCircle /></span>
                        </div>
                        <div className="cps">
                          {lesson.completed_tools.map(tool => <Chip key={tool} toolKey={tool} />)}
                        </div>
                        {lesson.scores?.length > 0 && lesson.scores.map((s, i) => {
                          const pct = s.total > 0 ? Math.round((s.score / s.total) * 100) : 0;
                          const c = col(pct);
                          const key = `${lesson.lesson_id}-${i}`;
                          const open = expandedScores[key];
                          return (
                            <div className="as" key={i} style={{ '--c': c }}>
                              <button className="as-b" aria-expanded={!!open}
                                onClick={() => setExpandedScores(p => ({ ...p, [key]: !p[key] }))}>
                                <Ring pct={pct} size={48} stroke={5} color={c}><b>{pct}%</b></Ring>
                                <h4>{TOOL_LABELS[s.tool_type] || s.tool_type}{s.attempt_num > 1 && ` (م${s.attempt_num})`}<small>{s.score}/{s.total}</small></h4>
                                {s.feedback && <ChevronUp />}
                              </button>
                              {open && s.feedback && (
                                <div className="co"><div><div className="fbx" data-testid={`feedback-${key}`}>{s.feedback}</div></div></div>
                              )}
                            </div>
                          );
                        })}
                      </section>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ===== Olympics ===== */}
            {profileTab === 'olympics' && (
              <div className="pn" role="tabpanel" data-testid="olympics-profile-stats">
                {olSubjects || olympicsStats?.subjects?.length > 0 ? (
                  <section className="cd">
                    <Head Icon={Trophy} title="أولمبياد رشيد" sub="نتائجك في كل مادة" c="var(--pk)" />
                    {olSubjects ? (
                      <>
                        <div className="kg">
                          <div className="kt" style={{ '--c': 'var(--easy)' }}><b>{ol.total_correct}</b><span>صحيحة</span></div>
                          <div className="kt" style={{ '--c': 'var(--hard)' }}><b>{ol.total_wrong}</b><span>خاطئة</span></div>
                          <div className="kt" style={{ '--c': 'var(--pu)' }}><b>{ol.total_attempts}</b><span>محاولات</span></div>
                          <div className="kt" style={{ '--c': 'var(--pk)' }}><b>{ol.total_time}ث</b><span>الوقت</span></div>
                        </div>
                        {ol.subjects.map(s => {
                          const p = s.total_questions > 0 ? Math.round((s.correct / s.total_questions) * 100) : 0;
                          return (
                            <div className="ob" key={s.subject_id} style={{ '--c': 'var(--hard)' }}>
                              <div className="ob-h">
                                <h3>{s.name_ar}</h3>
                                {s.rank > 0 && <span className="rk"><Medal />#{s.rank} من {s.total_grade_students}</span>}
                              </div>
                              <div className="ms">
                                <span style={{ '--c': 'var(--easy)' }}>{s.correct} صح</span>
                                <span style={{ '--c': 'var(--hard)' }}>{s.wrong} خطأ</span>
                                <span style={{ '--c': 'var(--pu)' }}>{s.attempts} محاولة</span>
                                <span style={{ '--c': 'var(--pk)' }}>{s.total_time}ث</span>
                              </div>
                              <div className="ob-f">
                                <span className="pbar" style={{ '--c': 'var(--hard)' }}><i style={{ width: `${Math.max(p, 2)}%` }} /></span>
                                <small>{s.correct}/{s.total_questions}</small>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <div className="kg">
                          <div className="kt" style={{ '--c': 'var(--easy)' }}><b>{olympicsStats.total_correct}</b><span>صحيحة</span></div>
                          <div className="kt" style={{ '--c': 'var(--pu)' }}><b>{olympicsStats.total_attempts}</b><span>محاولات</span></div>
                        </div>
                        {olympicsStats.subjects.map(s => (
                          <div className="ob" key={s.subject_id} style={{ '--c': 'var(--hard)' }}>
                            <div className="ob-h"><h3>{s.name_ar}</h3></div>
                            <div className="ob-f">
                              <span className="pbar" style={{ '--c': 'var(--hard)' }}><i style={{ width: `${Math.max(s.pct, 2)}%` }} /></span>
                              <small>{s.correct}/{s.total_questions}</small>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    <div className="ob-a"><button className="bt" onClick={() => navigate('/grades')}>متابعة الحل<ArrowRight style={{ transform: 'scaleX(-1)' }} /></button></div>
                  </section>
                ) : (
                  <section className="cd">
                    <div className="emp"><div className="t"><Trophy /></div><b>لم يتم تفعيل الأولمبياد بعد</b><span>سيظهر هنا تقدمك فور توفر أسئلة الأولمبياد.</span></div>
                  </section>
                )}
              </div>
            )}

            {/* ===== Weekly plans ===== */}
            {profileTab === 'weekly-plans' && (
              <div className="pn" role="tabpanel" data-testid="student-weekly-plans-tab">
                <section className="cd">
                  <Head Icon={CalendarDays} title="الخطط الأسبوعية من المعلمين" c="var(--easy)" />
                  {teacherContent?.weekly_plans?.length > 0 ? teacherContent.weekly_plans.map(plan => (
                    <div key={plan.id} style={{ marginBottom: 16 }}>
                      <div className="pl-h">
                        <div><b>{plan.week_label || 'الخطة الأسبوعية'}</b><p>من المعلم: {plan.teacher_name}</p></div>
                        {plan.is_seen ? (
                          <span className="vw ok"><Eye />تم الاطلاع</span>
                        ) : (
                          <button className="vw" data-testid={`mark-seen-${plan.id}`} onClick={async () => {
                            await axios.post(`${API_URL}/api/student/weekly-plans/${plan.id}/seen`, {}, { headers: getAuthHeaders() });
                            setTeacherContent(prev => ({ ...prev, weekly_plans: prev.weekly_plans.map(p => p.id === plan.id ? { ...p, is_seen: true } : p) }));
                          }}><Eye />تأكيد الاطلاع</button>
                        )}
                      </div>
                      {plan.days && (
                        <div className="tbw">
                          <table className="pt2">
                            <thead><tr><th>الحصة</th>{plan.days.map(d => <th key={d.day}>{d.day}</th>)}</tr></thead>
                            <tbody>
                              {(plan.days[0]?.periods || []).map((_, pi) => (
                                <tr key={pi}>
                                  <td className="w">{plan.period_labels?.[pi] || `ح${pi + 1}`}</td>
                                  {plan.days.map((d, di) => (
                                    <td key={di}>
                                      {(d.periods[pi]?.subject || d.periods[pi]?.topic) && (
                                        <div className="pc"><b>{d.periods[pi]?.subject}</b><span>{d.periods[pi]?.topic}</span></div>
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )) : (
                    <div className="emp"><div className="t"><CalendarDays /></div><b>لا توجد خطة أسبوعية حالياً</b><span>ستظهر خطط معلميك هنا.</span></div>
                  )}
                </section>
              </div>
            )}

            {/* ===== Assignments ===== */}
            {profileTab === 'assignments' && (
              <div className="pn" role="tabpanel" data-testid="student-assignments-tab">
                <section className="cd">
                  <Head Icon={ClipboardList} title="الواجبات والمهام" c="var(--pu)"
                    tr={teacherContent?.assignments?.length ? `${teacherContent.assignments.filter(a => a.is_read).length}/${teacherContent.assignments.length} مقروءة` : null} />
                  {teacherContent?.assignments?.length > 0 ? (
                    <div className="ls2">
                      {teacherContent.assignments.map(a => (
                        <div className="hw" key={a.id}>
                          <div className="hw-t">
                            <h3>{a.title}</h3>
                            {a.subject_name && <span className="sj2">{a.subject_name}</span>}
                            {a.is_read ? (
                              <span className="rd2"><CheckCircle />تم القراءة</span>
                            ) : (
                              <button className="rd2 un" data-testid={`mark-read-${a.id}`} onClick={async () => {
                                await axios.post(`${API_URL}/api/student/assignments/${a.id}/read`, {}, { headers: getAuthHeaders() });
                                setTeacherContent(prev => ({ ...prev, assignments: prev.assignments.map(x => x.id === a.id ? { ...x, is_read: true } : x) }));
                              }}><Clock />تحديد كمقروء</button>
                            )}
                          </div>
                          {a.description && <p>{a.description}</p>}
                          {a.files?.length > 0 && (
                            <div className="fl2">
                              {a.files.map((f, idx) => (
                                <a key={idx} href={`${API_URL}/api/assignments/${a.id}/download/${idx}?token=${localStorage.getItem('rasheedgpt_token')}`}
                                  target="_blank" rel="noopener noreferrer" data-testid={`student-file-download-${a.id}-${idx}`}>
                                  <Paperclip />{f.filename}<Download />
                                </a>
                              ))}
                            </div>
                          )}
                          <div className="mt2">
                            {a.due_date && <span><Clock />التسليم: <span className="dd">{a.due_date}</span></span>}
                            <span><User />من: {a.teacher_name}</span>
                            <span>أُضيف: {new Date(a.created_at).toLocaleDateString('ar')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="emp"><div className="t"><ClipboardList /></div><b>لا توجد واجبات حالياً</b><span>ستظهر واجبات معلميك هنا.</span></div>
                  )}
                </section>
              </div>
            )}

            {/* ===== Notes ===== */}
            {profileTab === 'notes' && (
              <div className="pn" role="tabpanel" data-testid="student-notes-tab">
                <section className="cd">
                  <Head Icon={StickyNote} title="ملاحظات المعلمين" sub="رسائل وتعليقات من معلميك على أدائك" c="var(--med)"
                    tr={teacherContent?.notes?.length ? `${teacherContent.notes.length} ملاحظة` : null} />
                  {teacherContent?.notes?.length > 0 ? (
                    <div className="ls2">
                      {teacherContent.notes.map(n => (
                        <div className="nt" key={n.id} style={{ '--c': 'var(--pu)' }}>
                          <div className="nt-h">
                            <div className="a"><User /></div>
                            <div className="who2"><b>{n.teacher_name}</b><span>المعلم</span></div>
                            <span className="dt"><CalendarDays />{new Date(n.created_at).toLocaleDateString('ar')}</span>
                          </div>
                          <div className="nt-m"><MessageSquare /><p>{n.note}</p></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="emp"><div className="t"><StickyNote /></div><b>لا توجد ملاحظات</b><span>ستظهر ملاحظات معلميك هنا.</span></div>
                  )}
                </section>
              </div>
            )}

            {/* ===== Contests ===== */}
            {profileTab === 'contests' && (
              <div className="pn" role="tabpanel" data-testid="student-contests-tab">
                <ContestsSection headers={getAuthHeaders()} token={token} />
              </div>
            )}

            {/* ===== Grades ===== */}
            {profileTab === 'grades-tab' && (
              <div className="pn" role="tabpanel" data-testid="student-grades-tab">
                <section className="cd">
                  <Head Icon={BarChart3} title="كشف العلامات" sub="نتائج الامتحانات والاختبارات" c="var(--pu)" />
                  {studentGrades.length > 0 ? (() => {
                    const avg = Math.round(studentGrades.reduce((a, g) => a + (g.max_mark > 0 ? g.mark / g.max_mark * 100 : 0), 0) / studentGrades.length);
                    const best = studentGrades.reduce((a, g) => (g.mark / g.max_mark > a.mark / a.max_mark ? g : a), studentGrades[0]);
                    const gr = avg >= 80 ? ['ممتاز', 'var(--easy)'] : avg >= 70 ? ['جيد', 'var(--med)'] : avg >= 50 ? ['مقبول', 'var(--med)'] : ['يحتاج تحسين', 'var(--hard)'];
                    return (
                      <>
                        <div className="gs">
                          <div className="kt" style={{ '--c': 'var(--pu)' }}><b>{studentGrades.length}</b><span>عدد الامتحانات</span></div>
                          <div className="kt" style={{ '--c': 'var(--easy)' }}><b>{avg}%</b><span>المعدل العام</span></div>
                          <div className="kt" style={{ '--c': 'var(--pk)' }}><b>{best.mark}/{best.max_mark}</b><span>أعلى علامة</span></div>
                          <div className="kt" style={{ '--c': gr[1] }}><b style={{ fontSize: 18 }}>{gr[0]}</b><span>التقدير</span></div>
                        </div>
                        <div className="ls2">
                          {studentGrades.map((g, idx) => {
                            const pct = g.max_mark > 0 ? Math.round((g.mark / g.max_mark) * 100) : 0;
                            const c = col(pct);
                            const gg = pct >= 80 ? 'ممتاز' : pct >= 70 ? 'جيد' : pct >= 50 ? 'مقبول' : 'يحتاج تحسين';
                            return (
                              <div className="ex" key={idx} style={{ '--c': c }}>
                                <div className="sc3"><b>{g.mark}</b><span>من {g.max_mark}</span></div>
                                <div>
                                  <h3>{g.exam_title}</h3>
                                  <div className="mm">{g.subject_name && <span className="sj2">{g.subject_name}</span>}<span>من: {g.teacher_name}</span></div>
                                </div>
                                <span className="gd2">{gg}</span>
                                <div className="bb"><span className="pbar"><i style={{ width: `${pct}%` }} /></span><b>{pct}%</b></div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })() : (
                    <div className="emp"><div className="t"><BarChart3 /></div><b>لا توجد علامات مسجلة</b><span>ستظهر علاماتك هنا فور رصدها.</span></div>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
